import React from 'react';
import UIInputSearch from '@/components/shared/UIInputSearch';
import UITenantTable from '@/components/shared/Chat/UITenantTable';
import UIPaginator from '@/components/shared/UIPaginator';
import type { TenantData } from '@/components/shared/Chat/UITenantTable';
import './ActiveConversationsList.scss';

export interface ConversationData {
  id: number;
  name: string;
  email: string;
  avatar?: string | null;
  unreadCount: number;
}

export interface ActiveConversationsListProps {
  /**
   * Список розмов
   */
  conversations?: ConversationData[];

  /**
   * Обробник кліку на розмову
   */
  onConversationClick?: (conversationId: number) => void;

  /**
   * Обробник кліку на кнопку інформації
   */
  onInfoClick?: (conversationId: number) => void;

  /**
   * Поточний пошуковий запит
   */
  searchTerm?: string;

  /**
   * Обробник пошуку
   */
  onSearch?: (searchTerm: string) => void;

  /**
   * Обробник зміни сторінки
   */
  onPageChange?: (page: number) => void;

  /**
   * Загальна кількість розмов
   */
  totalItems?: number;

  /**
   * Кількість елементів на сторінці
   */
  itemsPerPage?: number;

  /**
   * Поточна сторінка
   */
  currentPage?: number;

  /**
   * Додатковий CSS клас
   */
  className?: string;
}

const baseClass = 'ActiveConversationsList';

const ActiveConversationsList: React.FC<ActiveConversationsListProps> = ({
                                                                           conversations = [],
                                                                           onConversationClick = (id) => console.log('Conversation clicked:', id),
                                                                           onInfoClick = (id) => console.log('Info clicked:', id),
                                                                           searchTerm = '',
                                                                           onSearch = (term) => console.log('Search:', term),
                                                                           onPageChange = (page) => console.log('Page changed:', page),
                                                                           totalItems = 0,
                                                                           itemsPerPage = 10,
                                                                           currentPage = 1,
                                                                           className = '',
                                                                         }) => {

  const handleSearchChange = (value: string) => {
    onSearch(value);
  };

  const handleSearchClear = () => {
    onSearch('');
  };

  const containerClasses = [baseClass, className].filter(Boolean).join(' ');

  // Конвертуємо ConversationData в TenantData для UITenantTable
  const tenantData: TenantData[] = conversations.map(conv => ({
    id: conv.id,
    name: conv.name,
    email: conv.email,
    avatar: conv.avatar,
    unreadCount: conv.unreadCount, // Додаємо unreadCount
  }));

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <div className={containerClasses}>
      {/* Заголовок */}
      <div className={`${baseClass}__header`}>
        <h4 className={`${baseClass}__title`}>
          Active conversations
        </h4>
        <hr className={`${baseClass}__divider`} />
      </div>

      {/* Пошук */}
      <div className={`${baseClass}__search`}>
        <UIInputSearch
          placeholder="Search"
          size="medium"
          fullWidth
          value={searchTerm}
          onChange={handleSearchChange}
          onClear={handleSearchClear}
        />
      </div>

      {/* Таблиця через UITenantTable */}
      <div className={`${baseClass}__table`}>
        <UITenantTable
          tenants={tenantData}
          onTenantClick={onConversationClick}
          onInfoClick={onInfoClick}
          showHeader={true}
          variant="default"
          showUnreadBadges={true}
        />
      </div>

      {/* Навігація через UIPaginator */}
      {totalPages > 1 && (
        <UIPaginator
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          pageInfo={`${currentPage} of ${totalPages}`}
        />
      )}
    </div>
  );
};

export default ActiveConversationsList;
